// The board's own help (Features 4, onboarding part 5: "Help that's always one click away"). One place for the words,
// read by the ? panel in the browser and by agents through read_help (worker/mcp.ts), so a person and their agent are
// told the same thing. Each board kind has a line on what it does and its own keys; each task ("Share a board as a
// web page") has its steps and, where the board has one, an action: what the button under the chat's answer, or the
// panel's row, opens. An action names a place the browser opens (src/help/actions.ts); tests/help.test.ts checks every
// one is a real place.
//
// Words a person reads: plain, short, American English, sentence case, no em dashes.

import { BOARD_KINDS } from "./boardKinds";
import { PLAN, countWords, sizeWords } from "./limits";
import type { BoardKind } from "./types";

/** The full docs, on the website. */
export const DOCS_URL = "https://contextbolt.com/docs/board";

/** The panels a help action may open, by the board's own names for them (src/state/store.ts Panel). */
export const HELP_PANELS = ["share", "theme", "connect", "instructions", "import", "newboard", "shortcuts", "gallery", "data"] as const;
export type HelpPanel = (typeof HELP_PANELS)[number];

/** Settings' parts a help action may open: its old tabs, which its sections still answer to (src/chrome/settings OLD). */
export const HELP_SETTINGS = ["account", "computer", "agents", "notifications", "keyboard", "voice", "keys", "trash", "board"] as const;
export type HelpSettings = (typeof HELP_SETTINGS)[number];

/** Connect's own pages a help action may open (src/panels/connect/Hub.tsx viewOf). */
export type ConnectArg = { tab: "ai" | "data" | "connected" } | { app: "claude-code" | "codex" } | { computers: true } | { folders: true } | { found: true };

/** What a help task's button does: always the user's own click, never the agent's. */
export type HelpAction =
  | { do: "panel"; panel: Exclude<HelpPanel, "connect">; label: string }
  | { do: "connect"; arg: ConnectArg; label: string }
  | { do: "settings"; tab: HelpSettings; label: string }
  | { do: "newboard"; kind?: BoardKind; label: string }
  | { do: "help"; label: string };

export interface HelpTask {
  /** a stable id, the one read_help takes: lowercase words and dashes */
  id: string;
  /** what it does, as a person would ask for it: "Share a board as a web page" */
  title: string;
  /** other words people search for it by */
  words: string;
  /** the steps, each a short sentence */
  steps: string[];
  /** the button that does it, where the board has one */
  action?: HelpAction;
  /** only on these board kinds (a deck's Present): left out, it is for every board */
  kinds?: BoardKind[];
}

export interface KindKey {
  keys: string;
  what: string;
}

export interface KindHelp {
  /** its name, as the new-board picker says it */
  name: string;
  /** what it does, in two short sentences */
  what: string;
  /** its own keys, the most useful first (the board's keys for every kind are in src/chrome/shortcuts.ts) */
  keys: KindKey[];
  /** what to say to the chat on it, for "What can I say?": the empty board's asks and the busy board's, from
   * src/onboarding/prompts.ts (each one a thing the board's tools do on Claude Code and Codex alike) */
  say: string[];
}

/**
 * Each board kind: what it is for and its own keys, from each kind's key handlers (src/boards/<kind>/). Keys are
 * written as on a Mac; src/help/keys.ts shows Ctrl, Shift and Alt elsewhere.
 */
export const KIND_HELP: Record<BoardKind, KindHelp> = {
  canvas: {
    name: "Canvas",
    what: "Widgets anywhere on an open board: numbers, charts, lists, notes and anything your agent builds. Sources and agents keep them current.",
    keys: [
      { keys: "A", what: "Add a widget" },
      { keys: "Double-click", what: "Add a widget there" },
      { keys: "N", what: "Sticky note" },
      { keys: "D", what: "Data and sources" },
      { keys: "⇧1", what: "See everything" },
      { keys: "⌘D", what: "Duplicate the selection" },
    ],
    say: ["Add the weather for London", "Add a to-do list for this week", "Add the Hacker News front page", "What does this board tell me today?", "Organize this board into sections"],
  },
  doc: {
    name: "Doc",
    what: "Pages inside pages, with to-dos, toggles, tables and live widgets. Write them yourself, or ask your agent to draft, tidy or sum up.",
    keys: [
      { keys: "/", what: "Add a heading, list, to-do, table, picture or widget" },
      { keys: "[[", what: "Link to another page" },
      { keys: "⌘ click", what: "Open a web link at once (or Open on its card)" },
      { keys: "Tab / ⇧Tab", what: "Indent or outdent a list item" },
      { keys: "⌘B / ⌘I", what: "Bold, italic" },
      { keys: "⇧⌘9", what: "Turn a line into a to-do" },
      { keys: ">> then space", what: "Start a toggle" },
    ],
    say: ["Write a page that plans my week", "Make a meeting notes page with to-dos", "Summarize this page", "Turn this page into a checklist", "Add a page of next steps"],
  },
  sheet: {
    name: "Sheet",
    what: "Rows, columns, tabs and formulas, like a spreadsheet. Your agent can fill it in and adds totals as formulas.",
    keys: [
      { keys: "Enter or F2", what: "Edit the cell, keeping what is in it" },
      { keys: "=", what: "Start a formula, such as =SUM(B2:B9)" },
      { keys: "Tab / Enter", what: "Save and move right or down" },
      { keys: "⌘D", what: "Fill down" },
      { keys: "⌘B / ⌘I", what: "Bold, italic" },
      { keys: "⌘K", what: "Add or change a cell's link; pressed again in the link box, the command bar" },
      { keys: "⌘F / ⌘⇧H", what: "Find, and replace" },
      { keys: "⇧F2", what: "Add or edit the cell's note" },
      { keys: "Delete", what: "Clear the selected cells" },
    ],
    say: ["Make a monthly budget with totals", "Set up a sheet to track my invoices", "Add a total under each column", "Sort this tab by the first column", "What stands out in this sheet?"],
  },
  slides: {
    name: "Slides",
    what: "A deck you and your agent write and design, ready to present or to download for PowerPoint or as a PDF.",
    keys: [
      { keys: "Enter", what: "New slide after this one" },
      { keys: "P", what: "Present from this slide" },
      { keys: "← / →", what: "Previous or next slide" },
      { keys: "⌘G / ⇧⌘G", what: "Group or ungroup the selected boxes" },
      { keys: "Tab / ⇧Tab", what: "Indent or outdent a bullet" },
      { keys: "N", what: "Show or hide your notes while presenting" },
    ],
    say: ["Draft a five-slide pitch for my product", "Add an agenda slide after the title", "Tighten the words on every slide", "Add a closing slide with next steps", "Make the title slide stand out"],
  },
  plan: {
    name: "Plan",
    what: "Plan a big build with your agent: the goal, the steps and the questions only you can answer. Then hand it to your coding agent and watch each step get built.",
    keys: [
      { keys: "Enter", what: "Send your note to the agent" },
      { keys: "⇧Enter", what: "New line in a note" },
      { keys: "/", what: "Add a heading, list or to-do in a page" },
      { keys: "[[", what: "Link to another page" },
    ],
    say: ["Break my build into steps", "Write the goal and what done looks like", "What's left to do on this plan?", "What is waiting on me?", "Break the next step into tasks"],
  },
  files: {
    name: "Files",
    what: "Your project folders as notes you read, search and link. Agents reach only the folders you choose.",
    keys: [
      { keys: "⌘O", what: "Open a note by its name" },
      { keys: "⇧⌘F", what: "Search inside your notes" },
      { keys: "⌥← / ⌥→", what: "Back and forward through notes" },
      { keys: "⌘S", what: "Save the note you are editing" },
      { keys: "[[", what: "Link to another note" },
    ],
    say: ["Find my notes about pricing", "Summarize what's in my project folder", "Summarize the note I have open", "List the to-dos in my notes"],
  },
  mindmap: {
    name: "Mind map",
    what: "One idea in the middle and everything around it. Grow it with the keyboard, or ask your agent to brainstorm.",
    keys: [
      { keys: "Tab", what: "Add an idea inside the selected one" },
      { keys: "Enter", what: "Add an idea below" },
      { keys: "Space or F2", what: "Edit the idea's words" },
      { keys: "⇧ + arrows", what: "Move between ideas" },
      { keys: "⌥↑ / ⌥↓", what: "Move an idea up or down" },
      { keys: "Delete", what: "Remove the idea" },
    ],
    say: ["Add five ideas around the main idea", "Brainstorm names for my product", "Break each idea into smaller ones", "Group these ideas into themes"],
  },
  agenda: {
    name: "Calendar",
    what: "Your events and tasks by day, week and month, or as a list, on your own clock. Click or drag across the hours to make one, type it in plain words, or ask your agent to find free time, book a meeting or move one.",
    keys: [
      { keys: "N", what: "New event: “Lunch with Sam Friday 1pm 1h” sets its day, time and length" },
      { keys: "T", what: "Today" },
      { keys: "← / →", what: "Previous or next day, week, month or four weeks of the list" },
      { keys: "M / W / D / L", what: "Month, week, day or list view" },
      { keys: "Enter on an event", what: "Open it" },
      { keys: "Delete", what: "Delete the open event" },
      { keys: "Esc", what: "Close the event" },
    ],
    say: ["What can I do on this calendar?", "What's on this week?", "Find an hour for deep work on Thursday", "Move my 3pm to tomorrow"],
  },
  calendar: {
    name: "Content calendar",
    what: "Plan, draft and track posts by day. Your agent drafts them; you post each one and mark it posted.",
    keys: [
      { keys: "N", what: "New post" },
      { keys: "T", what: "Today" },
      { keys: "← / →", what: "Previous or next month or week" },
      { keys: "M / W / S", what: "Month, week or status view" },
      { keys: "I", what: "Ideas" },
    ],
    say: ["Draft three posts for this week", "Give me ten post ideas", "What's planned for next week?", "Turn my newest idea into a draft"],
  },
  pipeline: {
    name: "Client pipeline",
    what: "Your clients as deals that move through columns, from first hello to won. Log calls and follow-ups, and ask your agent who to chase.",
    keys: [
      { keys: "Enter on a deal", what: "Open it" },
      { keys: "⌘Enter", what: "Log your note in the deal's history" },
      { keys: "Esc", what: "Close the deal" },
    ],
    say: ["Add my first three clients", "What should I follow up on this week?", "Which deals have gone quiet?", "Rename the columns to fit how I sell"],
  },
  custom: {
    name: "Custom",
    what: "A tool your agent builds to fit how you work: a content pipeline with your own stages, a client intake, a review queue. What you type into it stays, on every computer, and Undo takes it back.",
    keys: [
      { keys: "⌘Z", what: "Undo the last change you made in it" },
      { keys: "⇧⌘Z", what: "Redo it" },
      { keys: "⌘J", what: "Open the chat, to ask your agent to change the app" },
    ],
    say: ["Build me a content pipeline with four stages", "Build a client intake board", "Move the three oldest records to Done", "Add a filter by stage"],
  },
};

/** The keys that work on every board, with what they do. */
export const EVERY_BOARD_KEYS: KindKey[] = [
  { keys: "⌘K", what: "Find anything, or ask your agent" },
  { keys: "⌘J", what: "Open or close the chat" },
  { keys: "G", what: "All boards" },
  { keys: "⌘Z", what: "Undo" },
  { keys: "?", what: "Every keyboard shortcut" },
];

/** What to say to the chat on any board, after the kind's own. */
export const EVERY_BOARD_SAY = ["How do I share this board as a web page?", "Make a doc board called Launch notes", "Change this board's theme to something calm"];

/**
 * How to do things, one entry a task. Each step is what the board does today: change it with the board. An entry with
 * kinds shows for those boards first in the ? panel; search finds every one.
 */
export const HELP_TASKS: HelpTask[] = [
  {
    id: "share-web-page",
    title: "Share a board as a web page",
    words: "share publish public link web page website send link anyone",
    steps: ["Open Share for the board.", "Turn on the public link.", "Under Show it as, pick Web page.", "Copy the link and send it. Anyone with it can look, and nobody can change it.", "Or ask your agent to make the link: it asks what you want first."],
    action: { do: "panel", panel: "share", label: "Open Share" },
  },
  {
    id: "share-people",
    title: "Share a board with someone",
    words: "share invite coworker teammate collaborate edit view email person",
    steps: ["Open Share for the board.", "Under People, type their email and choose Can view or Can edit.", "They open it by signing in with that email.", "Or ask your agent to share it: it asks what they can do first."],
    action: { do: "panel", panel: "share", label: "Open Share" },
  },
  {
    id: "connect-agent",
    title: "Connect Claude Code or Codex",
    words: "connect agent claude code codex chatgpt cursor mcp link computer ai",
    steps: ["Open Connect and choose Your AI.", "Pick Claude Code or Codex and follow its one step.", "Once it is linked, the chat beside the board can run on it, on your own plan."],
    action: { do: "connect", arg: { tab: "ai" }, label: "Open Connect" },
  },
  {
    id: "use-my-mcps",
    title: "Let the chat read my email or calendar",
    words: "email gmail calendar inbox mcp servers my existing mcps own tools",
    steps: ["Open Connect, then your agent's page (Claude Code or Codex).", "Turn on Use my existing MCPs.", "The chat then reaches the same tools your agent has on your computer."],
    action: { do: "connect", arg: { app: "claude-code" }, label: "Open Claude Code's page" },
  },
  {
    id: "connect-data",
    title: "Show live data from Stripe, an API or a feed",
    words: "data stripe api feed rss source service live numbers analytics connect",
    steps: ["Open Connect and choose Your data.", "Pick a service, a feed or any JSON API, and try it.", "Add the widget it suggests. It refreshes while the board is open."],
    action: { do: "connect", arg: { tab: "data" }, label: "Open Your data" },
  },
  {
    id: "add-widget",
    title: "Add a widget",
    words: "add widget chart number list note tile gallery new",
    kinds: ["canvas", "mindmap"],
    steps: ["Press A, or click Add a widget on the left.", "Pick one, or describe it and your agent builds it.", "Drag it where you want it."],
    action: { do: "panel", panel: "gallery", label: "Add a widget" },
  },
  {
    id: "arrange-board",
    title: "Tidy or arrange a board",
    words: "arrange organize tidy sort group sections layout move widgets",
    kinds: ["canvas"],
    steps: ["Ask the chat “Arrange this board into sections”.", "It moves every widget under its heading in one step, and leaves widgets you locked where they are.", "Say “Put it back” to undo it."],
  },
  {
    id: "new-board",
    title: "Make a new board",
    words: "new board create make doc sheet slides plan mind map calendar pipeline kind",
    steps: ["Open the boards list from the board's name, or press G for All boards.", "Choose New board, then a kind: canvas, doc, sheet, slides, plan and the rest."],
    action: { do: "newboard", label: "New board" },
  },
  {
    id: "instructions",
    title: "Tell agents how to work on a board",
    words: "instructions rules agents prompt guidance board instructions for agents",
    steps: ["Open Instructions for agents from the board's menu.", "Write what every agent should know on this board, in your own words.", "Agents follow them from their next read of the board. Earlier versions are kept, and one click restores any of them."],
    action: { do: "panel", panel: "instructions", label: "Open Instructions for agents" },
  },
  {
    id: "default-model",
    title: "Change the model the chat starts on",
    words: "model default claude codex opus sonnet haiku gpt settings agents",
    steps: ["Open Settings, then Agents.", "Under Default models, pick one for Claude and one for Codex.", "New chats, jobs and Buttons start on it."],
    action: { do: "settings", tab: "agents", label: "Open Settings, Agents" },
  },
  {
    id: "theme",
    title: "Change how a board looks",
    words: "theme look colour color background picture dark light style scene font",
    steps: ["Open Look and themes, or press ⇧T to try themes on.", "Pick a theme, or change its color, glass, type and scene.", "Each board keeps its own look."],
    action: { do: "panel", panel: "theme", label: "Open Look and themes" },
  },
  {
    id: "hands-free",
    title: "Let an agent run a task without asking each step",
    words: "hands free hands-free allow approve ask every step without asking automatic trust",
    steps: ["In the chat's menu (the line under the chat box), turn on Hands-free. For a Button or a job, turn on Hands-free in its settings.", "It uses your tools without stopping to ask. A chat or a Button still asks before anything that sends, deletes or buys; a job leaves those out and says so.", "Settings, Agents sets it for new chats, Buttons and jobs."],
    action: { do: "settings", tab: "agents", label: "Open Settings, Agents" },
  },
  {
    id: "button",
    title: "Make a Button that runs a task",
    words: "button update one click task automation run",
    steps: ["Add a widget and pick Button.", "Write its task in plain words, like “Read my inbox and update the deals”.", "Each press runs it with your agent and says what changed."],
    action: { do: "panel", panel: "gallery", label: "Add a widget" },
  },
  {
    id: "job",
    title: "Run a task every day or every week",
    words: "job schedule recurring every day week report automation cron agent job",
    steps: ["Add a widget and pick Agent job, or right-click a widget and choose Give it a job.", "Write what your agent should do and when.", "It runs on your computer and shows its latest answer."],
    action: { do: "panel", panel: "gallery", label: "Add a widget" },
  },
  {
    id: "import",
    title: "Bring in docs, notes or chats",
    words: "import bring in notes docs chats chatgpt claude markdown files upload",
    steps: ["Open New board and choose Import, under the kinds.", "Drop the files, a folder or a zip: Google Docs, Notion, Word, Obsidian, ChatGPT or Claude chats, sheets and PowerPoint decks.", "Check what it will make, then Import."],
    action: { do: "panel", panel: "import", label: "Bring something in" },
  },
  {
    id: "folders",
    title: "Let agents read my project folders",
    words: "folders files project vault read write computer obsidian notes",
    steps: ["Open Connect and choose Folders.", "Pick a folder on your computer, and whether agents may only read it or also change it.", "Agents reach nothing outside the folders you choose."],
    action: { do: "connect", arg: { folders: true }, label: "Choose folders" },
  },
  {
    id: "computers",
    title: "Link another computer or server",
    words: "computer server laptop link another machine runner sessions",
    steps: ["Open Connect and choose Computers.", "Run the one line it shows on that computer.", "Every Claude Code and Codex session on it, and what waits for you, then shows in one place."],
    action: { do: "connect", arg: { computers: true }, label: "Link a computer" },
  },
  {
    id: "undo-agent",
    title: "Undo what an agent did",
    words: "undo revert agent change mistake put back history version page",
    steps: ["Under the chat's reply, choose Undo: the widgets and boards go back as they were.", "An agent in another app (Claude Code, Codex, Cursor) that cleared or wrote over a sheet's cells: choose Undo on its line in Board activity.", "A page an agent wrote over: open the page's menu, then Page history, on a computer or a phone.", "Anything deleted stays in the trash for 30 days."],
  },
  {
    id: "limits",
    title: "What are my plan's limits?",
    words: "limit limits cap caps quota how many boards storage space full pictures files gb pages writes pushes push_data refresh refreshes slowed",
    steps: [
      `Up to ${PLAN.boards} boards, and ${countWords(PLAN.entriesPerBoard)} pages on each doc board. Archive or delete a board to make room for another.`,
      `${sizeWords(PLAN.pictureBytes)} for pictures and files, ${sizeWords(PLAN.dataBytes)} for board data and ${sizeWords(PLAN.chatBytes)} for chat history. When one is full, new things wait until you delete some. Nothing you have is ever deleted.`,
      `Agents and scripts can send up to ${countWords(PLAN.pushesPerDay)} updates a day, and sources refresh up to ${countWords(PLAN.pullsPerDay)} times a day. Past either, they wait until midnight UTC.`,
      `Past ${countWords(PLAN.writesPerMonth)} writes in a month, agents, scripts, sources and Google calendars slow to a little each day until the month ends. Your own edits always save.`,
      "Settings, Account shows what you have used.",
    ],
    action: { do: "settings", tab: "account", label: "Open Settings, Account" },
  },
  {
    id: "trash",
    title: "Get back something I deleted",
    words: "trash deleted restore recover bin lost cells tab sheet overwritten",
    steps: ["Open Settings, then Trash and backups.", "Deleted widgets, boards, pages and deals stay there for 30 days, and so do a sheet's tabs and the cells an agent cleared, wrote over or deleted.", "Choose Restore on the one you want. Cells go back where nobody has changed them since."],
    action: { do: "settings", tab: "trash", label: "Open the trash" },
  },
  {
    id: "voice",
    title: "Talk to the board",
    words: "voice talk speak microphone mic dictation hands free speech",
    steps: ["Open Settings, then Voice, and turn it on for this computer.", "Hold or press its key and say what you want, like “Go to Content calendar”.", "Anything it doesn't know goes to your agent."],
    action: { do: "settings", tab: "voice", label: "Open Settings, Voice" },
  },
  {
    id: "notifications",
    title: "Get reminders and notifications",
    words: "notifications reminders alerts bell push quiet hours",
    steps: ["Open Settings, then Notifications.", "Choose what raises one, and quiet hours.", "Everything also waits in the bell, top right."],
    action: { do: "settings", tab: "notifications", label: "Open Settings, Notifications" },
  },
  {
    id: "shortcuts",
    title: "See every keyboard shortcut",
    words: "keys keyboard shortcuts hotkeys list",
    steps: ["Press ? on any board, outside a text box.", "The list shows the keys for every board; the ? panel shows this board's own."],
    action: { do: "panel", panel: "shortcuts", label: "Show the shortcuts" },
  },
  {
    id: "present",
    title: "Present a deck",
    words: "present presentation slideshow full screen speaker notes",
    kinds: ["slides"],
    steps: ["Press P, or choose Present over the slide.", "Arrows or Space move on; N shows your notes; Esc stops."],
  },
  {
    id: "sheet-formulas",
    title: "Use formulas in a sheet",
    words: "formula formulas function functions google sheets excel missing name error query importrange filter xlookup",
    kinds: ["sheet"],
    steps: [
      "Type = in a cell, then a function's first letters: the list under it says what each does, and Tab puts one in.",
      "The everyday functions work as in Google Sheets: sums and counts (SUM, SUMIFS, COUNTIF, AVERAGE), lookups and lists (XLOOKUP, XMATCH, INDEX, FILTER, UNIQUE, SORT, SORTBY, SEQUENCE, CHOOSE, OFFSET, INDIRECT, LET), text (SPLIT, TEXTJOIN, REGEXMATCH, REGEXREPLACE, REGEXEXTRACT), dates (TODAY, WORKDAY, NETWORKDAYS, DAYS, DATEVALUE, ISOWEEKNUM), numbers (ROUND, STDEV, PERCENTILE, RANK, LARGE, RANDBETWEEN) and links (HYPERLINK).",
      "Not here: QUERY, IMPORTRANGE, IMPORTXML, IMPORTHTML, IMAGE, SPARKLINE and GOOGLEFINANCE, since they reach other files, the web or Google's own services. FILTER, SORT and SUMIFS do most of what QUERY does, a chart of the cells (right-click them, then Insert chart) draws what SPARKLINE draws, and your agent can fetch from the web for you.",
      "ARRAYFORMULA works but isn't needed: a formula over a range, such as =A2:A9*2, already fills the cells under it.",
    ],
  },
  {
    id: "sheet-links",
    title: "Put a link in a sheet's cell",
    words: "link links hyperlink url web address click open gmail cell",
    kinds: ["sheet"],
    steps: [
      "A cell that holds a web or mail address is a link already, and so is =HYPERLINK(\"https://…\", \"label\").",
      "To link a cell's own words, select it and press Ctrl or ⌘ K, or right-click it and choose Insert link.",
      "A click selects the cell, as ever; open the link from the card under it, or with Ctrl or ⌘ and a click.",
    ],
  },
  {
    id: "doc-links",
    title: "Put a web link in a page",
    words: "link links hyperlink url web address website email mailto gmail thread open click page doc",
    kinds: ["doc", "plan"],
    steps: [
      "Paste a web or mail address and it becomes a link. To link words, select them and press Link in the bar over the page.",
      "Your agent writes one as [words](https://…), with the whole address, and [[Page title]] to link another page.",
      "A click puts the cursor in a web link, so you can change its words. Its card shows under it: Open opens it in a new tab, and Ctrl or ⌘ and a click opens it at once.",
      "Edit and Remove are on the same card. A page link opens on a plain click.",
    ],
  },
  {
    id: "sheet-dates",
    title: "Change how a sheet's dates read",
    words: "date dates day first month first uk us british american format style 28/10/2026",
    kinds: ["sheet"],
    steps: [
      "A new sheet reads typed dates the way your region writes them, from your time zone: day first in the UK (28/10/2026), month first in the US.",
      "Open Number format, 123 above the cells. Date style shows a date as 28/10/2026, 28 Oct 2026 or 2026-10-28.",
      "Typed dates on this sheet switches the whole sheet between day first and month first.",
    ],
  },
  {
    // Fixes 10: full screen in the tab, from a button in sight
    id: "full-screen",
    title: "Make a board fill the screen",
    words: "full screen fullscreen maximize maximise bigger expand focus distraction free tab",
    steps: [
      "On a doc, sheet, deck or files board, press Full screen beside All boards, or F: the board fills the tab, and Back or Esc returns you to where you were.",
      "Beside Back, Hide the browser too goes full screen. On other boards, F or Full screen in the board's ⋯ menu fills the screen at once.",
    ],
  },
  {
    // Fixes 9, H2b: asked how to download a sheet, help gave the deck's steps
    id: "download-sheet",
    title: "Download a sheet as CSV or Excel",
    words: "download export save csv excel xlsx spreadsheet sheet tab file google sheets numbers",
    kinds: ["sheet"],
    steps: [
      "On a computer, press Download in the sheet's own tools at the left, under Import.",
      "This tab as CSV saves the tab you have open, each value as it shows.",
      "The whole sheet as Excel (.xlsx) saves every tab with its formulas, number formats, column widths, bold, borders, merged cells, hidden rows and columns, links, dropdowns, checkboxes and notes.",
    ],
  },
  // Fixes 10: the sheet's own tools, one entry each, so a person and their agent find the right steps
  {
    id: "sheet-find",
    title: "Find and replace in a sheet",
    words: "find search replace look up text value sheet",
    kinds: ["sheet"],
    steps: ["Press Ctrl or ⌘ F to find in the sheet, and Ctrl H (⌘ ⇧ H on a Mac) to replace."],
  },
  {
    id: "sheet-filter",
    title: "Filter a sheet's rows",
    words: "filter rows hide show only values column sheet",
    kinds: ["sheet"],
    steps: ["Right-click the cells, then Data, Create a filter.", "Each column's header gets a filter button for the values to show. Data, Remove filter takes it off."],
  },
  {
    id: "sheet-checkboxes",
    title: "Add checkboxes or a dropdown to cells",
    words: "checkbox checkboxes tick dropdown drop-down list choices validation options sheet",
    kinds: ["sheet"],
    steps: ["Right-click the cells, then Data, Insert checkboxes or Dropdown."],
  },
  {
    id: "sheet-notes",
    title: "Add a note to a cell",
    words: "note notes comment comments cell sheet",
    kinds: ["sheet"],
    steps: ["Right-click the cell and choose Insert note, or press ⇧ F2. Point at the cell to read it."],
  },
  {
    id: "sheet-color-rules",
    title: "Color cells by a rule",
    words: "color colour rule conditional formatting highlight fill cells greater than sheet",
    kinds: ["sheet"],
    steps: ["Right-click the cells, then Data, Color by rule. It is under Fill color in the bar above the cells too."],
  },
  {
    id: "sheet-hide",
    title: "Hide rows or columns in a sheet",
    words: "hide unhide show rows columns sheet",
    kinds: ["sheet"],
    steps: ["Right-click a row's number or a column's letter and choose Hide. The small arrows where they were show them again."],
  },
  {
    id: "sheet-merge",
    title: "Merge cells in a sheet",
    words: "merge unmerge combine cells sheet",
    kinds: ["sheet"],
    steps: ["Select the cells, then Align and merge in the bar above them: Merge all, Merge horizontally, Merge vertically or Unmerge."],
  },
  {
    id: "sheet-names",
    title: "Name a range for formulas",
    words: "named range name range formula sheet",
    kinds: ["sheet"],
    steps: ["Right-click the cells, then Data, Name this range. A formula on any tab then uses it by its name, as =SUM(Rates)."],
  },
  {
    id: "sheet-tables",
    title: "Make a table in a sheet",
    words: "table tables header column formula total sheet",
    kinds: ["sheet"],
    steps: ["Right-click a block with a header row, then Data, Make a table.", "A formula in one cell of a column fills every row, new ones too, and =SUM(Orders[Total]) reads a column by its header."],
  },
  {
    id: "sheet-charts",
    title: "Chart a sheet's cells",
    words: "chart graph plot bar line pie sheet",
    kinds: ["sheet"],
    steps: ["Select the cells, right-click them and choose Insert chart. It sits beside them and follows their values."],
  },
  {
    id: "download-deck",
    title: "Download a deck for PowerPoint or as a PDF",
    words: "download export powerpoint pptx pdf keynote deck",
    kinds: ["slides"],
    steps: ["Open the ⋯ menu over the slides (Import and download).", "Under Download, choose PDF or PowerPoint (.pptx)."],
  },
  {
    id: "hand-off-plan",
    title: "Hand a plan to my coding agent",
    words: "hand off plan build coding agent claude code codex steps",
    kinds: ["plan"],
    steps: ["Once the plan's steps are written, press Hand off.", "Your coding agent builds it step by step and keeps the plan up to date.", "Its questions wait for you on the plan."],
  },
  {
    id: "add-event",
    title: "Add an event to a Calendar",
    words: "add new event meeting appointment calendar book schedule lunch call",
    kinds: ["agenda"],
    steps: ["Press N and type it as you would say it, like “Lunch with Sam Friday 1pm 1h”, then press Enter.", "Or click a time in the week for an hour there, or drag across the hours for as long as it lasts.", "Drag an event to move it, and its foot to change when it ends. Ctrl or Cmd Z undoes any change."],
  },
  {
    id: "connect-google-calendar",
    title: "Connect Google Calendar to a Calendar board",
    words: "google calendar gcal connect sync link add account events reconnect permissions",
    kinds: ["agenda"],
    steps: [
      "Open your Calendar board and click Add a Google account at the foot of the Calendars column. Or open Connect, then Your data, then Google Calendar.",
      "Sign in to Google and tick both permissions it asks for: your list of calendars, and seeing and editing your events. The board needs both.",
      "Its calendars show on the board and stay up to date. Tick which ones show in the Calendars column, and add as many accounts as you use. If one says it stopped syncing, click Reconnect.",
    ],
  },
  {
    id: "calendar-layers",
    title: "See posts and follow-ups on a Calendar",
    words: "layers posts follow ups deals content calendar pipeline hide show",
    kinds: ["agenda"],
    steps: ["Your content calendars and pipelines show on a Calendar board by themselves, each a layer under Layers in the calendars column.", "Untick a layer to hide it on this board.", "Drag a post or follow-up to move it on its own board, or click it to open it there."],
  },
  {
    id: "plan-day",
    title: "Plan my day",
    words: "plan day schedule tasks today time blocks agenda",
    kinds: ["agenda"],
    steps: ["Open your Calendar board and ask the chat “Plan my day”, with the tasks you want done.", "It reads what is on your day and puts each task in a free time, as an event on the board.", "Or say where one goes: “Put invoice Acme on Friday at 2pm for 30 minutes”. Drag any of them to move it."],
  },
  {
    id: "rename-columns",
    title: "Rename a pipeline's columns",
    words: "columns stages rename pipeline sales steps",
    kinds: ["pipeline"],
    steps: ["Ask the chat “Rename the columns to Lead, Call, Proposal, Won”.", "Every deal stays in its column. The won column stays last."],
  },
  {
    id: "pipeline-currency",
    title: "Change a pipeline's currency",
    words: "currency money pounds euros dollars gbp eur usd pipeline values deal worth change",
    kinds: ["pipeline"],
    steps: [
      "A new pipeline starts in your region's currency: pounds in the UK, euros in the euro area, and dollars anywhere else.",
      "To change it, open Stages and currency (the sliders on the left, or the board's ⋯ menu) and pick one. Values show in the new currency as they were written: none is converted.",
      "Or ask the chat “Show this pipeline in euros”. A value written in another currency is refused, so nothing is saved in the wrong one.",
    ],
  },
  {
    id: "mark-posted",
    title: "Mark a post as posted",
    words: "posted published post content calendar status",
    kinds: ["calendar"],
    steps: ["The board never publishes for you.", "Post it where it goes, then open the post on the calendar and mark it posted."],
  },
  {
    id: "delete-board",
    title: "Delete a board",
    words: "delete remove erase get rid of board archived agent ask tidy",
    steps: [
      "Open the board's menu (⋯ beside its name at the top) and choose Delete. In the boards list and All boards, a right-click on a board offers Delete too. On a phone: More, then Delete this board.",
      "An archived board: open Archived at the end of the boards list or of All boards, and choose Delete beside the board.",
      "Or ask your agent, naming the board: “Delete the board Old launch”. Asked to tidy up your boards, it names each board it would delete first, and deletes only the ones you say yes to. Board activity shows each delete, with Undo.",
      "It stays in the trash for 30 days, and Settings, Trash and backups brings it back. Your last board that isn't archived can't be deleted.",
    ],
  },
  {
    id: "archive-board",
    title: "Archive a board",
    words: "archive archived put away hide old unarchive restore shelf",
    steps: [
      "In the boards list (click the board's name at the top), right-click the board and choose Archive. The board's own menu (⋯) has Archive too.",
      "It leaves the list, kept just as it was and read only.",
      "To bring it back, open Archived at the end of the boards list or of All boards and choose Restore beside it.",
    ],
  },
  {
    id: "board-file",
    title: "Download a board as a file",
    words: "download export backup board file json copy import",
    steps: ["Open Settings, then Trash and backups.", "Under Export or import a board, save this board as a file, or make a board from one."],
    action: { do: "settings", tab: "board", label: "Open Settings, Trash and backups" },
  },
  {
    id: "another-device",
    title: "Use the board on my phone or another computer",
    words: "phone mobile another device laptop sign in email",
    steps: ["Save an email under Settings, Account, if you haven't.", "On the other device, open the board and sign in with that email.", "Your boards, chats and where you left off come with you."],
    action: { do: "settings", tab: "account", label: "Open Settings, Account" },
  },
  {
    id: "help-panel",
    title: "Find help for the board I'm on",
    words: "help docs guide how to question what can i say",
    steps: ["Click ? at the top right, or Help in the menu under your initial when the boards show as tabs.", "It shows what this kind of board does, its keys and things to ask, and searches all of the board's help."],
    action: { do: "help", label: "Open help" },
  },
];

const norm = (s: string) => s.toLowerCase().normalize("NFKD").replace(/[̀-ͯ]/g, "").replace(/[^a-z0-9 ]+/g, " ");
const STOP = new Set(["how", "do", "i", "can", "the", "a", "an", "to", "my", "of", "on", "in", "is", "it", "this", "what", "where", "and", "or", "for", "with", "me", "you", "your", "as", "at", "by", "be", "up", "from", "into", "get", "want", "make"]);

/**
 * Help entries that match words, best first: a task's title counts most, then its words, then its steps. kind puts
 * that kind's own entries first among equals. Empty words list nothing.
 */
export function searchHelp(q: string, kind?: BoardKind, max = 6): HelpTask[] {
  const words = norm(q)
    .split(/\s+/)
    .filter((w) => w.length > 1 && !STOP.has(w));
  if (!words.length) return [];
  const scored = offeredTasks(kind).map((t) => {
    const title = norm(t.title);
    const tags = norm(t.words);
    const steps = norm(t.steps.join(" "));
    let s = 0;
    for (const w of words) {
      const stem = w.length > 4 ? w.replace(/(ing|es|s|ed)$/, "") : w;
      if (title.split(" ").some((x) => x.startsWith(stem))) s += 3;
      else if (tags.split(" ").some((x) => x.startsWith(stem))) s += 2;
      else if (steps.includes(stem)) s += 1;
    }
    if (s && kind && t.kinds?.includes(kind)) s += 0.5;
    return { t, s };
  })
    .filter((x) => x.s > 0)
    .sort((a, b) => b.s - a.s);
  // the close ones only: a match on one small word of several is not an answer
  const top = scored[0]?.s ?? 0;
  return scored
    .filter((x) => x.s >= Math.max(2, top * 0.4))
    .slice(0, max)
    .map((x) => x.t);
}

/** A question's words that say what it is about, as search reads them. */
const askedWords = (q: string) =>
  norm(q)
    .split(/\s+/)
    .filter((w) => w.length > 1 && !STOP.has(w))
    .map((w) => (w.length > 4 ? w.replace(/(ing|es|s|ed)$/, "") : w));

/**
 * Whether an entry answers a question, not just shares a word with it (Fixes 9, 28 Oct: "how do I change the pipeline
 * currency to pounds" was answered with how to change the chat's model): it holds every word the question is about,
 * or all but one of three or more.
 */
export function answersQuestion(q: string, t: HelpTask): boolean {
  const words = askedWords(q);
  if (!words.length) return false;
  const title = norm(t.title).split(" ");
  const tags = norm(t.words).split(" ");
  const steps = norm(t.steps.join(" "));
  const held = words.filter((w) => title.some((x) => x.startsWith(w)) || tags.some((x) => x.startsWith(w)) || steps.includes(w)).length;
  return held >= (words.length >= 3 ? words.length - 1 : words.length);
}

/** A task by its id. */
export const helpTask = (id: string): HelpTask | undefined => HELP_TASKS.find((t) => t.id === id);

/** The tasks for a board kind: its own first, then the ones for every board. */
export function tasksFor(kind: BoardKind): HelpTask[] {
  return [...HELP_TASKS.filter((t) => t.kinds?.includes(kind)), ...HELP_TASKS.filter((t) => !t.kinds)];
}

/** The tasks a user may be told of: every one but those only about kinds not finished yet; on a board of such a kind, its own show. */
export function offeredTasks(here?: BoardKind): HelpTask[] {
  return HELP_TASKS.filter((t) => !t.kinds || t.kinds.some((k) => BOARD_KINDS[k].ready || k === here));
}

/** A task as an agent reads it: its title, id, steps and what its button opens. */
export function taskText(t: HelpTask): string {
  return [`${t.title} (id ${t.id})`, ...t.steps.map((s, i) => `${i + 1}. ${s}`), ...(t.action ? [`In the board's own chat, a button for this, “${t.action.label}”, shows under your reply.`] : [])].join("\n");
}
